import React, { useContext, memo, useCallback, useMemo } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import articlesData from '../data/javascripts/articlesData';
import { useNavigate } from 'react-router-dom';

// Import separated components
import ActionButton from '../widgets/ActionButton';
import ProfileImage from '../widgets/ProfileImage';
import ArticlePreviewCard from '../widgets/ArticlePreviewCard';

const Hero = () => {
    const { darkMode } = useContext(ThemeContext);
    const navigate = useNavigate();
    
    // Theme classes are derived from the global body/section styles
    const heroBgClass = 'bg-bg-light dark:bg-bg-dark text-text-light dark:text-text-dark';

    const handleArticleClick = useCallback((url) => {
        navigate(`/articles/${url}`);
    }, [navigate]);

    const handleViewAllArticles = useCallback(() => {
        navigate('/articles');
    }, [navigate]);

    // Get latest 3 articles for preview
    const latestArticles = useMemo(() => articlesData.slice(0, 3), []);

    // Theme-aware styles for the callout block
    const calloutClass = useMemo(() => ({
        bg: darkMode ? 'bg-secondary/10' : 'bg-gray-100/70',
        border: 'border-primary dark:border-secondary',
    }), [darkMode]);
    
    return (
        <section 
            id="home" 
            className={`flex items-center relative min-h-screen lg:py-32 overflow-hidden ${heroBgClass}`}
        >
            {/* Background Image Overlay */}
            <div
                className="absolute inset-0 w-full h-full"
                style={{
                    backgroundImage: 'url(/images/mesh-tunnel.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    opacity: darkMode ? '0.08' : '0.06',
                    zIndex: 0,
                    filter: 'blur(0.5px)'
                }}
            />

            <div className="container mx-auto px-4 relative z-10">
                <div className="flex flex-col lg:flex-row items-center">
                    
                    {/* Left Column - Content (lg:w-7/12) */}
                    <div className="w-full lg:w-7/12 mb-10 lg:mb-0 pr-0 lg:pr-10">
                        <div className="fade-in max-w-xl mx-auto lg:mx-0 text-center lg:text-left">
                            
                            {/* Profile Image */}
                            <div className="mb-6">
                                <ProfileImage/>
                            </div>

                            <p className="uppercase mb-2 text-sm tracking-widest opacity-60 font-medium text-muted-light dark:text-muted-dark">
                                Strengthen the Mind in a Changing World
                            </p>
                            
                            <h1 className="text-4xl sm:text-5xl font-normal mb-6 leading-tight tracking-[-0.5px]">
                                Mental Models for{' '}
                                <span className="text-primary font-medium dark:text-secondary">
                                    Living in clarity
                                </span>
                            </h1>
                            
                            {/* Quote Block */}
                            <div className={`mb-6 p-4 rounded-lg border-l-4 ${calloutClass.border} ${calloutClass.bg}`}>
                                <p className="mb-3 italic text-lg opacity-95">
                                    "An object in motion continues to be in motion unless something comes along the way."
                                </p>
                                <p className="mb-0 text-md opacity-85">
                                    People who behave and believe in something will continue to operate similarly unless something dramatic happens in their lives. Changing beliefs needs effort—the push may come from internal conflicts or external pressure.
                                </p>
                            </div>

                            {/* Main Content Text */}
                            <div className="text-md leading-relaxed opacity-90">
                                <p className="mb-4">
                                    <strong className="font-medium">Change is continuous until it reaches equilibrium.</strong> The initial excitement fades in marriage, and over time, both settle into unwritten agreements.
                                </p>
                                
                                <p className="mb-0">
                                    <strong className="font-medium">The law of cause and effect.</strong> Today affects tomorrow and not the other way around. Millions of years of evolution shaped our brain to understand this reality and become good at predicting what may happen next. The brain simulates reality within and quickly renders the future state of the system based on the initial condition.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Article Previews (lg:w-5/12) */}
                    <div className="w-full lg:w-5/12">
                        <div className="fade-in max-w-lg mx-auto lg:mx-0">
                            <div className="flex justify-between items-center mb-3">
                                <h5 className={`text-xl font-semibold mb-0 text-text-light dark:text-text-dark`}>
                                    Latest Writings
                                </h5>
                            </div>
                            
                            <p className={`text-sm mb-4 text-muted-light dark:text-muted-dark opacity-70`}>
                                Explore recent insights on mental models and systems thinking
                            </p>

                            <div className="space-y-4">
                                {latestArticles.map((article) => (
                                    <div key={article.path}>
                                        <ArticlePreviewCard
                                            article={article}
                                            darkMode={darkMode}
                                            onArticleClick={handleArticleClick}
                                        />
                                    </div>
                                ))}
                            </div>

                            {/* Additional subtle call-to-action */}
                            <div 
                                className={`mt-6 p-4 text-center rounded-lg border border-border-light dark:border-border-dark ${darkMode ? 'bg-card-dark' : 'bg-card-light'}`} 
                            >
                                <p className="text-sm mb-3 opacity-70 text-muted-light dark:text-muted-dark">
                                    Dive deeper into all articles or reach me...
                                </p>
                                
                                {/* Button Group */}
                                <div className="flex justify-center space-x-4"> 
                                    <ActionButton 
                                        text="Brose All" 
                                        onClick={handleViewAllArticles}
                                        className="btn-md"
                                    />
                                    <ActionButton 
                                        text="Connect" 
                                        className="btn-secondary-outline btn-md" // Using secondary outline button for contrast
                                        targetId="contact"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default memo(Hero);